import React, { useState, useMemo } from 'react';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useFinancialBookings, useExpenses } from '../hooks/useFinancials';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ExportMenu } from './ExportMenu';
import { DateRangeControl } from './DateRangeControl';

// Format currency
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', AED: 'AED ', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || '$';
  return `${symbol}${Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format date for display in header
const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const ProfitLossTab = ({ onNavigate }) => {
  // Date range - default to current year
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      startDate: `${now.getFullYear()}-01-01`,
      endDate: `${now.getFullYear()}-12-31`
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  
  // Get currency from org
  // Currency from the operator's country (single source of truth)
  const { currencyCode: currency } = useOperatorLocale();
  
  // Fetch via shared financial hooks — SAME canonical cache keys as Revenue
  // (financial-bookings) and Expenses (financial-expenses), so all three tabs
  // dedupe to one request each and stay perfectly in sync.
  const { bookings, summary: backendSummary, isLoading: bookingsLoading, isError: bookingsError } =
    useFinancialBookings(dateRange.startDate, dateRange.endDate);
  const { expenses, isLoading: expensesLoading, isError: expensesError } =
    useExpenses(dateRange.startDate, dateRange.endDate);

  const isLoading = bookingsLoading || expensesLoading;
  const error = bookingsError || expensesError;

  // 🔒 SSOT: headline money figures come ONLY from the backend-computed summary
  // (financial_utils). Line-item breakdowns (subtotal/tax/fees) are summed from
  // the same backend booking rows — there is NO divergent client-side fallback.
  const totals = useMemo(() => {
    const expenseTotal = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const revenue = backendSummary
      ? (backendSummary.totalOwnerRevenue ?? backendSummary.totalRevenue ?? 0)
      : 0;
    const netProfit = revenue - expenseTotal;
    const profitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
    const expensePercent = revenue > 0 ? (expenseTotal / revenue) * 100 : 0;

    // Fees: prefer summary totals, else sum from the same backend booking rows
    const stripeFee = backendSummary?.totalStripeFees ?? bookings.reduce((sum, b) => sum + (b.stripeFee || 0), 0);
    const platformFee = backendSummary?.totalPlatformFees ?? bookings.reduce((sum, b) => sum + (b.platformFee || 0), 0);
    const serviceFee = bookings.reduce((sum, b) => sum + (b.serviceFee || 0), 0);
    const totalFees = stripeFee + platformFee + serviceFee;
    const feePercent = revenue > 0 ? (totalFees / revenue) * 100 : 0;

    // Subtotal & tax derived from the same backend booking rows
    const subtotal = bookings.reduce((sum, b) => sum + (b.subtotal || b.basePrice || 0), 0);
    const tax = bookings.reduce((sum, b) => sum + (b.salesTax || 0), 0);

    return {
      revenue, subtotal, tax, serviceFee, expenseTotal, netProfit,
      profitMargin, expensePercent, stripeFee, platformFee, totalFees, feePercent,
      bookingCount: bookings.filter(b => b.paymentStatus === 'paid').length,
      expenseCount: expenses.length
    };
  }, [bookings, expenses, backendSummary]);
  
  // Recent items (last 5)
  const recentBookings = bookings.filter(b => b.paymentStatus === 'paid').slice(0, 5);
  const recentExpenses = expenses.slice(0, 5);
  
  // Export functions
  const exportCSV = () => {
    const headers = ['Category', 'Amount'];
    const rows = [
      ['Total Revenue', totals.revenue],
      ['Subtotal', totals.subtotal],
      ['Tax', totals.tax],
      ['Service Fee', totals.serviceFee],
      ['Total Expenses', totals.expenseTotal],
      ['Stripe Fees', totals.stripeFee],
      ['Platform Fees', totals.platformFee],
      ['Net Profit', totals.netProfit],
      ['Profit Margin %', totals.profitMargin.toFixed(1)]
    ];
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pnl_summary_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    toast.success('P&L exported as CSV!');
  };
  
  const exportJSON = () => {
    const exportData = {
      period: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      summary: {
        revenue: totals.revenue,
        subtotal: totals.subtotal,
        tax: totals.tax,
        serviceFee: totals.serviceFee,
        expenses: totals.expenseTotal,
        stripeFees: totals.stripeFee,
        platformFees: totals.platformFee,
        netProfit: totals.netProfit,
        profitMargin: totals.profitMargin
      },
      bookingCount: totals.bookingCount,
      expenseCount: totals.expenseCount
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pnl_summary_${dateRange.startDate}_to_${dateRange.endDate}.json`;
    a.click();
    toast.success('P&L exported as JSON!');
  };
  
  const exportPDF = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Profit & Loss Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 600px; margin: 0 auto; }
          h1 { color: #1a1a1a; border-bottom: 1px solid #10b981; padding-bottom: 10px; }
          .period { color: #6b7280; margin-bottom: 20px; }
          .section { background: #f9fafb; border-radius: 8px; padding: 20px; margin: 15px 0; }
          .section h3 { margin: 0 0 15px 0; color: #374151; font-size: 14px; text-transform: uppercase; }
          .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
          .row:last-child { border-bottom: none; }
          .label { color: #6b7280; }
          .value { font-weight: 600; }
          .value.positive { color: #10b981; }
          .value.negative { color: #ef4444; }
          .highlight { background: #10b981; color: white; padding: 20px; border-radius: 8px; text-align: center; margin: 20px 0; }
          .highlight .amount { font-size: 32px; font-weight: bold; }
          .highlight .label { color: rgba(255,255,255,0.8); font-size: 14px; }
        </style>
      </head>
      <body>
        <h1>Profit & Loss Report</h1>
        <p class="period">${dateRange.startDate} to ${dateRange.endDate}</p>
        
        <div class="highlight">
          <div class="amount">${formatCurrency(totals.netProfit, currency)}</div>
          <div class="label">Net Profit</div>
        </div>
        
        <div class="section">
          <h3>Revenue</h3>
          <div class="row"><span class="label">Total Revenue</span><span class="value positive">${formatCurrency(totals.revenue, currency)}</span></div>
          <div class="row"><span class="label">Subtotal</span><span class="value">${formatCurrency(totals.subtotal, currency)}</span></div>
          <div class="row"><span class="label">Tax Collected</span><span class="value">${formatCurrency(totals.tax, currency)}</span></div>
        </div>
        
        <div class="section">
          <h3>Processing Fees</h3>
          <div class="row"><span class="label">Stripe Fees</span><span class="value negative">${formatCurrency(totals.stripeFee, currency)}</span></div>
          <div class="row"><span class="label">Platform Fees</span><span class="value negative">${formatCurrency(totals.platformFee, currency)}</span></div>
          <div class="row"><span class="label">Service Fees</span><span class="value">${formatCurrency(totals.serviceFee, currency)}</span></div>
        </div>
        
        <div class="section">
          <h3>Expenses</h3>
          <div class="row"><span class="label">Total Expenses</span><span class="value negative">${formatCurrency(totals.expenseTotal, currency)}</span></div>
        </div>
        
        <div class="section">
          <h3>Summary</h3>
          <div class="row"><span class="label">Profit Margin</span><span class="value">${totals.profitMargin.toFixed(1)}%</span></div>
          <div class="row"><span class="label">Bookings</span><span class="value">${totals.bookingCount}</span></div>
          <div class="row"><span class="label">Expense Items</span><span class="value">${totals.expenseCount}</span></div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    toast.success('Opening print dialog for PDF...');
  };
  
  // Error handling
  if (error) {
    toast.error('Failed to load financial data');
  }
  
  if (isLoading) {
    return (
      <TabPageLayout title="Profit & Loss" subtitle="Your financial overview">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  const isProfitable = totals.netProfit >= 0;
  
  // Calculate bar widths for the stacked revenue bar
  const total = totals.subtotal + totals.tax + totals.expenseTotal;
  const subtotalWidth = total > 0 ? (totals.subtotal / total) * 100 : 0;
  const taxWidth = total > 0 ? (totals.tax / total) * 100 : 0;
  const expenseWidth = total > 0 ? (totals.expenseTotal / total) * 100 : 0;
  
  // Fee bar widths (proportional to revenue)
  const feeBarWidth = totals.feePercent;
  const platformFeeWidth = totals.totalFees > 0 ? (totals.platformFee / totals.totalFees) * feeBarWidth : 0;
  const stripeFeeWidth = totals.totalFees > 0 ? (totals.stripeFee / totals.totalFees) * feeBarWidth : 0;
  const serviceFeeWidth = totals.totalFees > 0 ? (totals.serviceFee / totals.totalFees) * feeBarWidth : 0;
  
  return (
    <TabPageLayout 
      title="Profit & Loss" 
      subtitle="Your financial overview"
      actions={
        <ExportMenu onExport={(f) => ({ csv: exportCSV, pdf: exportPDF, json: exportJSON }[f]())} />
      }
    >
      <div className="space-y-6">
        {/* Date Range Picker */}
        <DateRangeControl
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onStartChange={(v) => setDateRange({ ...dateRange, startDate: v })}
          onEndChange={(v) => setDateRange({ ...dateRange, endDate: v })}
        />
        
        {/* P&L Visual Card */}
        <div className="card-soft p-6">
          {/* Header with date range and Net Profit in top right */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-500 text-sm">
              {formatDateRange(dateRange.startDate, dateRange.endDate)} Performance
            </p>
            <div className={`px-4 py-2 rounded-full ${isProfitable ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <span className={`text-lg font-medium ${isProfitable ? 'text-emerald-700' : 'text-red-600'}`}>
                {isProfitable ? '' : '-'}{formatCurrency(Math.abs(totals.netProfit), currency)}
              </span>
              <span className={`text-sm ml-2 ${isProfitable ? 'text-emerald-600/70' : 'text-red-500/70'}`}>
                Net {isProfitable ? 'Profit' : 'Loss'}
              </span>
            </div>
          </div>
          
          {/* Revenue/Expenses Bar */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-emerald-600 font-medium">Revenue: {formatCurrency(totals.revenue, currency)}</span>
              <span className="text-red-500 font-medium">Expenses: {formatCurrency(totals.expenseTotal, currency)}</span>
            </div>
            
            {/* Stacked Bar */}
            <div className="h-5 bg-gray-100 rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${subtotalWidth}%` }}
                title={`Subtotal: ${formatCurrency(totals.subtotal, currency)}`}
              />
              <div 
                className="h-full bg-amber-500 transition-all duration-500"
                style={{ width: `${taxWidth}%` }}
                title={`Tax: ${formatCurrency(totals.tax, currency)}`}
              />
              <div 
                className="h-full bg-red-500 transition-all duration-500"
                style={{ width: `${expenseWidth}%` }}
                title={`Expenses: ${formatCurrency(totals.expenseTotal, currency)}`}
              />
            </div>
            
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>Net: {formatCurrency(totals.netProfit, currency)}</span>
              <span>{totals.expensePercent.toFixed(1)}% spent</span>
            </div>
          </div>
          
          {/* Processing Fees Bar */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Processing Fees:</span>
              <span className="text-gray-900 font-medium">{formatCurrency(totals.totalFees, currency)}</span>
            </div>
            
            <div className="h-5 bg-gray-100 rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-blue-500 transition-all duration-500"
                style={{ width: `${platformFeeWidth}%` }}
                title={`BookingFlows: ${formatCurrency(totals.platformFee, currency)}`}
              />
              <div 
                className="h-full bg-purple-500 transition-all duration-500"
                style={{ width: `${stripeFeeWidth}%` }}
                title={`Stripe: ${formatCurrency(totals.stripeFee, currency)}`}
              />
              <div 
                className="h-full bg-yellow-500 transition-all duration-500"
                style={{ width: `${serviceFeeWidth}%` }}
                title={`Service Fee: ${formatCurrency(totals.serviceFee, currency)}`}
              />
            </div>
            
            <div className="flex items-center text-xs">
              <div className="flex items-center gap-4">
                <span className="text-blue-600">BookingFlows: {formatCurrency(totals.platformFee, currency)}</span>
                <span className="text-purple-600">Stripe: {formatCurrency(totals.stripeFee, currency)}</span>
                {totals.serviceFee > 0 && (
                  <span className="text-amber-600">Service: {formatCurrency(totals.serviceFee, currency)}</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Bottom Stats */}
          <div className="flex flex-wrap items-center justify-between pt-4 border-t border-gray-100 gap-4">
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span className="text-gray-500">Subtotal: <span className="text-gray-900 font-medium">{formatCurrency(totals.subtotal, currency)}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span className="text-gray-500">Tax: <span className="text-gray-900 font-medium">{formatCurrency(totals.tax, currency)}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-500">Expenses: <span className="text-gray-900 font-medium">{formatCurrency(totals.expenseTotal, currency)}</span></span>
              </div>
            </div>
            <div className="text-sm text-gray-400">
              {totals.bookingCount} bookings · {totals.expenseCount} expenses
            </div>
          </div>
        </div>
        
        {/* Recent Revenue & Expenses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent Revenue */}
          <div className="card-soft overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Recent Revenue</h3>
              <button 
                onClick={() => onNavigate && onNavigate('revenue')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                See All <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {recentBookings.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">No revenue yet</p>
              ) : (
                recentBookings.map((booking) => (
                  <div key={booking.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <p className="text-sm text-gray-900">{booking.customerName || booking.customer_name || 'Customer'}</p>
                      <p className="text-xs text-gray-500">{formatDate(booking.bookingDate || booking.booking_date)}</p>
                    </div>
                    <span className="text-sm text-green-600">
                      +{formatCurrency(booking.totalAmount || booking.total_amount, currency)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Recent Expenses */}
          <div className="card-soft overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Recent Expenses</h3>
              <button 
                onClick={() => onNavigate && onNavigate('expenses')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                Add Expense <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {recentExpenses.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">No expenses yet</p>
              ) : (
                recentExpenses.map((expense) => (
                  <div key={expense.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <p className="text-sm text-gray-900">{expense.title || expense.description || 'Expense'}</p>
                      <p className="text-xs text-gray-500">{formatDate(expense.date || expense.expense_date)}</p>
                    </div>
                    <span className="text-sm text-red-600">
                      -{formatCurrency(expense.amount, currency)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </TabPageLayout>
  );
};

export default ProfitLossTab;
